import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Plus } from "lucide-react";
import { useScope } from "../context/ScopeContext";

export default function AddTransactionSheet({ open, onOpenChange }) {
  const { scope } = useScope();
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [type, setType] = useState("gasto"); // gasto | ingreso | transferencia
  const [accountId, setAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setType("gasto");
    setCategoryId("");
    setAmount("");
    setNote("");
    setToAccountId("");
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/accounts", { params: { scope } }).then(({ data }) => {
      setAccounts(data);
      if (data.length && !accountId) setAccountId(data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope]);

  // Fetch categories whenever scope or type changes (only ingreso/gasto have categories)
  useEffect(() => {
    if (!open) return;
    if (type === "transferencia") {
      setCategories([]);
      setCategoryId("");
      return;
    }
    api.get("/categories", { params: { scope, kind: type } })
      .then(({ data }) => setCategories(data))
      .catch(() => setCategories([]));
    // Reset selected category when switching type (cat from gasto can't be used for ingreso)
    setCategoryId("");
  }, [open, scope, type]);

  // Auto-pick the other account as destination when switching to transferencia
  useEffect(() => {
    if (type === "transferencia" && accountId && !toAccountId && accounts.length > 1) {
      const other = accounts.find((a) => a.id !== accountId);
      if (other) setToAccountId(other.id);
    }
  }, [type, accountId, toAccountId, accounts]);

  const destinationOptions = useMemo(
    () => accounts.filter((a) => a.id !== accountId),
    [accounts, accountId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountId || !amount) {
      toast.error("Completa cuenta y monto");
      return;
    }
    if (type === "transferencia" && !toAccountId) {
      toast.error("Elige la cuenta destino");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        account_id: accountId,
        type,
        amount: Number(amount),
        note,
      };
      if (type === "transferencia") {
        payload.transfer_to_account_id = toAccountId;
        payload.category = "Transferencia";
      } else if (categoryId) {
        payload.category_id = categoryId;
      }
      await api.post("/transactions", payload);
      toast.success(
        type === "transferencia" ? "Transferencia registrada" : "Movimiento agregado"
      );
      reset();
      onOpenChange(false);
      window.dispatchEvent(new CustomEvent("fp:tx-created"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="add-tx-sheet"
        className="bg-bone border-line rounded-t-3xl p-0 max-w-md mx-auto overflow-y-auto overscroll-contain"
        style={{ maxHeight: "92dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 1.5rem)" }}
      >
        <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-line/80" aria-hidden />
        <SheetHeader className="text-left px-6 pt-2">
          <SheetTitle className="font-display text-2xl text-ink">Nuevo movimiento</SheetTitle>
          <SheetDescription className="text-soft text-sm">
            Registra un ingreso, gasto o transferencia.
          </SheetDescription>
        </SheetHeader>

        {/* Scope context banner — siempre visible para evitar capturas en scope equivocado */}
        <div
          data-testid="add-tx-scope-banner"
          className={`mx-6 mb-3 rounded-2xl border px-4 py-2.5 flex items-center gap-2 text-xs ${
            scope === "shared"
              ? "bg-sage-50 border-sage-100 text-sage-900"
              : "bg-bone border-line text-muted2"
          }`}
        >
          <span className="font-bold uppercase tracking-[0.16em]">
            {scope === "shared" ? "Espacio compartido" : "Espacio personal"}
          </span>
          <span className="text-soft">·</span>
          <span>
            {scope === "shared"
              ? "Va al dinero común"
              : "Solo lo verás tú"}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-8 space-y-5">
          {/* Type toggle */}
          <div className="grid grid-cols-3 gap-1 bg-white border border-line rounded-2xl p-1">
            <TypeButton
              testid="tx-type-gasto"
              active={type === "gasto"}
              icon={ArrowUpRight}
              label="Gasto"
              onClick={() => setType("gasto")}
            />
            <TypeButton
              testid="tx-type-ingreso"
              active={type === "ingreso"}
              icon={ArrowDownLeft}
              label="Ingreso"
              onClick={() => setType("ingreso")}
            />
            <TypeButton
              testid="tx-type-transferencia"
              active={type === "transferencia"}
              icon={ArrowLeftRight}
              label="Trans."
              onClick={() => setType("transferencia")}
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
              Monto
            </label>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-2xl text-soft">$</span>
              <input
                data-testid="tx-amount-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 bg-transparent outline-none border-b-2 border-line focus:border-sage-500 font-display text-3xl font-extrabold tabular py-1"
              />
            </div>
            {amount && (
              <p className="text-xs text-soft mt-1">{formatMXN(Number(amount) || 0)}</p>
            )}
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
              {type === "transferencia" ? "Desde la cuenta" : "Cuenta"}
            </label>
            <select
              data-testid="tx-account-select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
            >
              {accounts.length === 0 && (
                <option value="">
                  {scope === "shared" ? "Sin cuentas compartidas — crea una primero" : "Sin cuentas aún"}
                </option>
              )}
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {type === "transferencia" ? (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Hacia la cuenta
              </label>
              <select
                data-testid="tx-to-account-select"
                value={toAccountId}
                onChange={(e) => setToAccountId(e.target.value)}
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
              >
                <option value="">Elige cuenta destino</option>
                {destinationOptions.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <p className="text-xs text-soft mt-2">
                Las transferencias entre tus cuentas no afectan ingresos ni gastos del mes.
              </p>
            </div>
          ) : (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                Categoría {categoryId ? "" : <span className="text-soft normal-case tracking-normal">· opcional</span>}
              </label>
              {categories.length === 0 ? (
                <div
                  data-testid="tx-no-categories"
                  className="bg-white border border-line rounded-2xl px-4 py-4 text-center"
                >
                  <p className="text-sm text-soft leading-relaxed">
                    Aún no tienes categorías de {type === "ingreso" ? "ingreso" : "gasto"} en este espacio.
                  </p>
                  <Link
                    to="/categorias"
                    onClick={() => onOpenChange(false)}
                    className="mt-2 inline-flex items-center gap-1.5 text-sage-700 font-bold text-sm hover:underline"
                    data-testid="tx-create-first-category"
                  >
                    <Plus size={14} /> Crear primera categoría
                  </Link>
                  <p className="text-[11px] text-soft mt-2">
                    Puedes guardar sin categoría si prefieres.
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="tx-category-none"
                    onClick={() => setCategoryId("")}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      categoryId === ""
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-soft hover:border-sage-200"
                    }`}
                  >
                    Sin categoría
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`tx-category-${c.id}`}
                      onClick={() => setCategoryId(c.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        categoryId === c.id
                          ? "bg-sage-900 text-bone border-sage-900"
                          : "bg-white border-line text-ink hover:border-sage-200"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                  <Link
                    to="/categorias"
                    onClick={() => onOpenChange(false)}
                    data-testid="tx-manage-categories"
                    className="px-3 py-1.5 rounded-full text-sm border border-dashed border-line text-soft hover:border-sage-300 hover:text-sage-700 inline-flex items-center gap-1.5"
                  >
                    <Plus size={12} /> Nueva
                  </Link>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
              Nota (opcional)
            </label>
            <input
              data-testid="tx-note-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Detalles del movimiento"
              className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="tx-submit-button"
            className="w-full bg-sage-900 text-bone font-bold py-4 rounded-2xl hover:bg-sage-900/90 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {submitting ? "Guardando…" : "Guardar movimiento"}
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function TypeButton({ active, icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-colors ${
        active ? "bg-sage-900 text-bone" : "text-ink hover:bg-sage-50"
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}
